import React, { use, useEffect, useState } from "react";
import { CompanyBalanceSheet } from "../../company";
import { useOutletContext } from "react-router-dom";
import { getBalanceSheet } from "../../api";
import RatioList from "../RatioList/RatioList";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import {
  formatLargeMonetaryNumber,
  formatLargeNonMonetaryNumber,
  formatRatio,
} from "../Helpers/NumberFormatting";

interface Props {}

const configs = [
  {
    label: <div className="font-bold">Total Assets</div>,
    subTitle: "Everything the company owns — both current and long-term",
    render: (company: CompanyBalanceSheet) =>
      formatLargeMonetaryNumber(Number(company.totalAssets || 0)),
  },
  {
    label: "Total Current Assets",
    subTitle: "Assets expected to be converted to cash within a year",
    render: (company: CompanyBalanceSheet) =>
      formatLargeMonetaryNumber(Number(company.totalCurrentAssets || 0)),
  },
  {
    label: "Cash & Short-Term Investments",
    subTitle: "Highly liquid assets including cash and near-cash instruments",
    render: (company: CompanyBalanceSheet) =>
      formatLargeMonetaryNumber(Number(company.cashAndShortTermInvestments || 0)),
  },
  {
    label: "Accounts Receivable",
    subTitle: "Money owed to the company by customers",
    render: (company: CompanyBalanceSheet) =>
      formatLargeMonetaryNumber(Number(company.accountsReceivables || 0)),
  },
  {
    label: "Inventory",
    subTitle: "Goods and materials the company intends to sell",
    render: (company: CompanyBalanceSheet) =>
      formatLargeMonetaryNumber(Number(company.inventory || 0)),
  },
  {
    label: "Property, Plant & Equipment",
    subTitle: "Long-term physical assets used in operations",
    render: (company: CompanyBalanceSheet) =>
      formatLargeMonetaryNumber(Number(company.propertyPlantEquipmentNet || 0)),
  },
  {
    label: "Long-Term Investments",
    subTitle: "Investments intended to be held for over one year",
    render: (company: CompanyBalanceSheet) =>
      formatLargeMonetaryNumber(Number(company.longTermInvestments || 0)),
  },
  {
    label: "Total Non-Current Assets",
    subTitle: "Assets providing value beyond one year",
    render: (company: CompanyBalanceSheet) =>
      formatLargeMonetaryNumber(Number(company.totalNonCurrentAssets || 0)),
  },

  {
    label: "Total Liabilities",
    subTitle: "All debts and obligations the company owes",
    render: (company: CompanyBalanceSheet) =>
      formatLargeMonetaryNumber(Number(company.totalLiabilities || 0)),
  },
  {
    label: "Total Current Liabilities",
    subTitle: "Obligations due within one year",
    render: (company: CompanyBalanceSheet) =>
      formatLargeMonetaryNumber(Number(company.totalCurrentLiabilities || 0)),
  },
  {
    label: "Accounts Payable",
    subTitle: "Money owed to suppliers and vendors",
    render: (company: CompanyBalanceSheet) =>
      formatLargeMonetaryNumber(Number(company.accountPayables || 0)),
  },
  {
    label: "Short-Term Debt",
    subTitle: "Debt obligations due within the next year",
    render: (company: CompanyBalanceSheet) =>
      formatLargeMonetaryNumber(Number(company.shortTermDebt || 0)),
  },
  {
    label: "Deferred Revenue",
    subTitle: "Payments received before services are rendered",
    render: (company: CompanyBalanceSheet) =>
      formatLargeMonetaryNumber(Number(company.deferredRevenue || 0)),
  },
  {
    label: "Total Non-Current Liabilities",
    subTitle: "Long-term obligations beyond one year",
    render: (company: CompanyBalanceSheet) =>
      formatLargeMonetaryNumber(Number(company.totalNonCurrentLiabilities || 0)),
  },

  {
    label: "Total Stockholders’ Equity",
    subTitle: "Net value belonging to shareholders",
    render: (company: CompanyBalanceSheet) =>
      formatLargeMonetaryNumber(Number(company.totalStockholdersEquity || 0)),
  },
  {
    label: "Common Stock",
    subTitle: "Value of common shares issued by the company",
    render: (company: CompanyBalanceSheet) =>
      formatLargeMonetaryNumber(Number(company.commonStock || 0)),
  },
  {
    label: "Retained Earnings",
    subTitle: "Profits kept in the business after dividends",
    render: (company: CompanyBalanceSheet) =>
      formatLargeMonetaryNumber(Number(company.retainedEarnings || 0)),
  },
  {
    label: "Accumulated Other Comprehensive Income",
    subTitle: "Unrealized gains or losses not reflected in net income",
    render: (company: CompanyBalanceSheet) =>
      formatLargeMonetaryNumber(Number(company.accumulatedOtherComprehensiveIncomeLoss || 0)),
  },
  {
    label: "Total Liabilities & Equity",
    subTitle: "The balance between what the company owns and owes",
    render: (company: CompanyBalanceSheet) =>
      formatLargeMonetaryNumber(Number(company.totalLiabilitiesAndTotalEquity || 0)),
  },
];

const BalanceSheet = (props: Props) => {
  const ticker = useOutletContext<string>();
  const [balanceSheet, setBalanceSheet] = useState<CompanyBalanceSheet>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  useEffect(() => {
    const fetchBalanceSheet = async () => {
      try {
        const result = await getBalanceSheet(ticker);
        setBalanceSheet(result?.data[0]);
      } catch (err: any) {
        setError(err.message || "Something went wrong while fetching data.");
      } finally {
        setLoading(false);
      }
    };
    fetchBalanceSheet();
  }, []);

  if (loading) {
    return (
      <div className="py-8 flex items-center justify-center">
        <LoadingSpinner size={50} />
      </div>
    );
  }

  if (error) {
    return <div className="text-red-600 font-medium">⚠️ Error fetching data: {error}</div>;
  }

  return (
    <>
      {balanceSheet ? (
        <>
          <RatioList data={balanceSheet} config={configs} />
        </>
      ) : (
        <>
          <div className="text-gray-500">No data available.</div>
        </>
      )}
    </>
  );
};

export default BalanceSheet;
